import { apiFetch } from './client';
import type { UserRole } from '@grid2/shared-types';

/**
 * Organization members + invitations client (ADR-0004 E9 IA split).
 *
 * Canonical replacement for the legacy `teamsApi` (which called the
 * `/api/teams/members|invitations` compatibility aliases). This client calls
 * `/api/org-members` + `/api/org-invitations` directly — the routes that
 * survive once the aliases are removed. `web/src/api/teams.ts` is now the
 * TRUE-teams client (ADR-0004 D2 Team entity); org-member/tenant-role
 * management lives here and is surfaced in Settings → Organization members.
 */

export interface OrgMember {
  userId: string;
  displayName: string;
  email: string;
  roles: UserRole[];
  status: 'active' | 'disabled';
  joinedAt: string;
}

export interface OrgInvitation {
  invitationId: string;
  email: string;
  role: UserRole;
  status: 'pending' | 'accepted' | 'expired';
  invitedBy: string;
  createdAt: string;
  expiresAt: string;
}

/** Response from a tenant-role change — carries the ADR-0004 R4 staleness note. */
export interface UpdateRolesResult {
  ok: boolean;
  roleStaleness: string;
}

/** Response from an involuntary offboard/disable — the reassignment + immediate session-revocation outcome. */
export interface DisableMemberResult {
  ok: boolean;
  offboard: { removedFrom: string[]; reassignedTeamIds: string[] };
  sessionRevocation: { recordsCleared: boolean; blacklistBroadcast: number };
  immediateRevocation: true;
}

/**
 * Result of accepting an invitation as an already-authenticated identity
 * (ADR-0010 Phase 3 onboarding "accept" path — `web/src/pages/OnboardingPage.tsx`).
 */
export interface AcceptInvitationResult {
  ok: boolean;
  orgId: string;
  userId: string;
  roles: string[];
}

/**
 * One of the signed-in user's OWN pending invitations into some org — the
 * post-login invite banner's row. Scoped server-side to the caller's
 * OAuth-verified email; the browser never supplies an email.
 */
export interface MyPendingInvitation {
  invitationId: string;
  orgId: string;
  orgName: string;
  role: UserRole;
  createdAt: string;
  expiresAt: string;
}

export const orgMembersApi = {
  listMembers: () => apiFetch<{ members: OrgMember[] }>('/api/org-members'),

  /**
   * Assigns tenant roles (System-Admin-only at the BFF — `requireRole('owner')`).
   * Ordinary role changes take effect within the 15-30 min access-token band;
   * the response's `roleStaleness` note should be surfaced to the caller.
   */
  updateRoles: (userId: string, roles: UserRole[]) =>
    apiFetch<UpdateRolesResult>(`/api/org-members/${userId}/roles`, {
      method: 'PUT',
      body: JSON.stringify({ roles }),
    }),

  /** Soft-removes an org member (admin+). */
  removeMember: (userId: string) =>
    apiFetch<void>(`/api/org-members/${userId}`, { method: 'DELETE' }),

  /**
   * Involuntary offboard + disable (System-Admin-only). May reject with
   * `409 OFFBOARD_REQUIRES_REASSIGNMENT` (`reassignTo` required) when the
   * user solely owns one or more teams.
   */
  disableMember: (userId: string, reassignTo?: string) =>
    apiFetch<DisableMemberResult>(`/api/org-members/${userId}/disable`, {
      method: 'POST',
      body: JSON.stringify({ reassignTo }),
    }),

  listInvitations: () => apiFetch<{ invitations: OrgInvitation[] }>('/api/org-invitations'),

  invite: (data: { email: string; role: UserRole }) =>
    apiFetch<OrgInvitation>('/api/org-invitations', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  cancelInvitation: (invitationId: string) =>
    apiFetch<void>(`/api/org-invitations/${invitationId}`, { method: 'DELETE' }),

  resendInvitation: (invitationId: string) =>
    apiFetch<{ ok: boolean }>(`/api/org-invitations/${invitationId}/resend`, {
      method: 'POST',
    }),

  /**
   * Accepts a pending invitation for the AUTHENTICATED caller (ADR-0010 Phase
   * 3 onboarding "accept" path). The caller's OAuth-verified email is resolved
   * server-side from the session — never sent from the browser.
   */
  accept: (invitationId: string) =>
    apiFetch<AcceptInvitationResult>(`/api/org-invitations/${encodeURIComponent(invitationId)}/accept`, {
      method: 'POST',
    }),

  /** The signed-in user's own pending, unexpired invitations (any org). */
  listMine: () => apiFetch<{ invitations: MyPendingInvitation[] }>('/api/org-invitations/mine'),

  /** Declines one of the signed-in user's own pending invitations. */
  decline: (invitationId: string) =>
    apiFetch<{ ok: boolean }>(`/api/org-invitations/${encodeURIComponent(invitationId)}/decline`, {
      method: 'POST',
    }),
};
